import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import DoneAllIcon from '@mui/icons-material/DoneAll';

const HISTORY_API_URL     = 'https://ab.pizzahub.lk/api/credit_customer_history.php';
const SETTLEMENTS_API_URL = 'https://ab.pizzahub.lk/api/credit_settlements.php';
const REMINDERS_API_URL   = 'https://ab.pizzahub.lk/api/credit_reminders.php';
const CLEAR_CREDIT_API_URL = 'https://ab.pizzahub.lk/api/credit_clear_customer.php';
const CLOSE_CYCLE_URL     = 'https://ab.pizzahub.lk/api/credit_close.php';
const SALES_API_URL       = 'https://ab.pizzahub.lk/api/sales_management.php';
const SMS_API_URL         = 'https://ab.pizzahub.lk/api/send_sms.php';

const fmt = (n) => Number(n || 0).toFixed(2);
const isCredit = (s) => String(s || '').toLowerCase() === 'credit';
const paymentMethodOptions = [
  { value: '', label: 'Not Specified' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'online', label: 'Online' },
];

function StatCard({ label, value, color }) {
  return (
    <Paper sx={{ p: 2, minWidth: 220, flex: '1 1 220px' }}>
      <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }} color={color || 'text.primary'}>
        {value}
      </Typography>
    </Paper>
  );
}

export default function CreditCustomerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams();
  const [search] = useSearchParams();

  const parsedCustomerId = Number(customerId || 0);
  const fallbackName = location.state?.customerName || '';

  const [filterFrom, setFilterFrom] = useState(search.get('date_from') || '');
  const [filterTo, setFilterTo] = useState(search.get('date_to') || '');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [history, setHistory] = useState(null);

  const [saleRef, setSaleRef] = useState('');
  const [saleAmt, setSaleAmt] = useState('');
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [saleMethod, setSaleMethod] = useState('');
  const [savingSale, setSavingSale] = useState(false);

  const [rcvAmt, setRcvAmt] = useState('');
  const [rcvDate, setRcvDate] = useState(new Date().toISOString().split('T')[0]);
  const [rcvNote, setRcvNote] = useState('');
  const [savingSettlement, setSavingSettlement] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [clearingCredit, setClearingCredit] = useState(false);
  const [sendingCust, setSendingCust] = useState(false);
  const [sendingInvId, setSendingInvId] = useState(null);

  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' });
  const showSnack = (severity, msg) => setSnack({ open: true, severity, msg });

  const loadHistory = useCallback(() => {
    if (!parsedCustomerId) {
      setLoading(false);
      setError('Invalid customer id');
      return Promise.resolve();
    }

    setLoading(true);
    setError('');

    const params = new URLSearchParams();
    params.append('customer_id', String(parsedCustomerId));
    if (filterFrom) params.append('date_from', filterFrom);
    if (filterTo) params.append('date_to', filterTo);

    return fetch(`${HISTORY_API_URL}?${params.toString()}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.status === 'ok') {
          setHistory(d);
        } else {
          setHistory(null);
          setError(d?.message || 'Failed to load history');
        }
      })
      .catch(() => {
        setHistory(null);
        setError('Network error while loading customer history');
      })
      .finally(() => setLoading(false));
  }, [parsedCustomerId, filterFrom, filterTo]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const customer = history?.customer || null;
  const unpaidInvoices = useMemo(
    () => (history?.sales || []).filter((s) => isCredit(s.payment_status)),
    [history]
  );
  const summary = history?.summary || {};
  const allSettlements = history?.settlements?.all || [];
  const closureLogs = history?.closure_logs || [];
  const reminders = history?.reminders || [];
  const ledger = history?.ledger || [];

  const goBack = () => {
    const q = new URLSearchParams();
    if (filterFrom) q.append('date_from', filterFrom);
    if (filterTo) q.append('date_to', filterTo);
    navigate(`/sales${q.toString() ? `?${q.toString()}` : ''}`);
  };

  const sendSMS = (to, text) =>
    fetch(`${SMS_API_URL}?to=${encodeURIComponent(to)}&text=${encodeURIComponent(text)}`)
      .then((r) => r.text());

  const logReminder = (saleId, message, apiResponse) =>
    fetch(REMINDERS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'add',
        customer_id: parsedCustomerId,
        sale_id: saleId ?? null,
        channel: 'sms',
        message,
        api_response: apiResponse,
      }),
    }).then((r) => r.json()).catch(() => ({ status: 'error' }));

  const addCreditSale = async () => {
    const amt = parseFloat(saleAmt);
    if (!amt || amt <= 0) {
      showSnack('warning', 'Enter a valid sale amount');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(saleDate)) {
      showSnack('warning', 'Select a valid sale date');
      return;
    }

    setSavingSale(true);
    try {
      const payload = {
        action: 'add',
        order_details: saleRef.trim(),
        amount: amt,
        payment_method: saleMethod,
        payment_status: 'Credit',
        customer: parsedCustomerId,
        created_at: saleDate,
      };

      const d = await fetch(SALES_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (d?.status !== 'success' && d?.status !== 'ok') {
        showSnack('error', d?.message || 'Failed to add credit sale');
        return;
      }

      setSaleRef('');
      setSaleAmt('');
      setSaleMethod('');
      showSnack('success', 'Credit sale added');
      await loadHistory();
    } catch {
      showSnack('error', 'Network error while adding sale');
    } finally {
      setSavingSale(false);
    }
  };

  const addSettlement = async () => {
    const amt = parseFloat(rcvAmt);
    if (!amt || amt <= 0) {
      showSnack('warning', 'Enter a valid amount');
      return;
    }

    setSavingSettlement(true);
    try {
      const res = await fetch(SETTLEMENTS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: parsedCustomerId,
          amount: amt,
          pay_date: rcvDate,
          note: rcvNote,
        }),
      });
      const d = await res.json();

      if (d.status !== 'ok') {
        showSnack('error', d.message || 'Failed to add settlement');
        return;
      }

      if (d.auto_closed) {
        const c = d.closure || {};
        showSnack(
          'success',
          `Fully settled. Credit Rs ${fmt(c.open_credit_total)}, received Rs ${fmt(c.received_total)}, SMS: ${c.sms_sent ? 'sent' : 'not sent'}`
        );
      } else {
        showSnack('success', 'Settlement saved');
      }

      setRcvAmt('');
      setRcvNote('');
      await loadHistory();
    } catch {
      showSnack('error', 'Network error');
    } finally {
      setSavingSettlement(false);
    }
  };

  const remindCustomer = async () => {
    if (!customer?.phone) {
      showSnack('warning', 'No phone number for this customer');
      return;
    }
    setSendingCust(true);
    try {
      const msg =
        `Dear ${customer.name}, your outstanding balance is Rs.${fmt(summary.open_balance)} ` +
        `for ${summary.open_credit_count || unpaidInvoices.length} invoice(s). Please settle at your earliest convenience.\n` +
        `If paying online:\nPizza Hub, 1000208076, Commercial Bank.\nThank you!`;
      const resp = await sendSMS(customer.phone, msg);
      await logReminder(null, msg, resp);
      showSnack('success', 'Customer reminder sent');
      await loadHistory();
    } catch {
      showSnack('error', 'Failed to send reminder');
    } finally {
      setSendingCust(false);
    }
  };

  const remindInvoice = async (inv) => {
    if (!customer?.phone) {
      showSnack('warning', 'No phone number for this customer');
      return;
    }
    setSendingInvId(inv.id);
    try {
      const invNo = inv.order_details || inv.id;
      const invDate = (inv.created_at || '').split('T')[0];
      const msg =
        `Dear ${customer.name}, a kind reminder to clear Rs.${fmt(inv.amount)} ` +
        `(Invoice #${invNo}, ${invDate}).\n` +
        `If paying online:\nPizza Hub, 1000208076, Commercial Bank.\nPlease note: invoice no. ${invNo}`;
      const resp = await sendSMS(customer.phone, msg);
      await logReminder(inv.id, msg, resp);
      showSnack('success', `Invoice reminder sent (#${invNo})`);
      await loadHistory();
    } catch {
      showSnack('error', 'Failed to send invoice reminder');
    } finally {
      setSendingInvId(null);
    }
  };

  const markAllAsPaid = async () => {
    if (!unpaidInvoices.length) {
      showSnack('info', 'No unpaid invoices');
      return;
    }
    setMarkingPaid(true);
    try {
      await Promise.all(unpaidInvoices.map((inv) => {
        const payload = {
          action: 'update',
          id: inv.id,
          order_details: inv.order_details,
          amount: inv.amount,
          payment_method: inv.payment_method || '',
          payment_status: 'paid',
          customer: parsedCustomerId,
          created_at: (inv.created_at || '').split('T')[0],
        };
        return fetch(SALES_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).then((r) => r.json());
      }));

      const closeRes = await fetch(CLOSE_CYCLE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: parsedCustomerId }),
      }).then((r) => r.json()).catch(() => ({ status: 'error' }));

      await loadHistory();
      if (closeRes?.status === 'ok') {
        showSnack(
          'success',
          `Invoices marked paid and receipts closed (${closeRes.closed_count || 0}). SMS: ${closeRes.sms_sent ? 'sent' : 'not sent'}`
        );
      } else {
        showSnack('warning', 'Invoices marked paid, but closing receipts failed');
      }
    } catch {
      showSnack('error', 'Failed to mark all as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const clearCreditData = async () => {
    if (!customer) return;

    const ok = window.confirm(
      `Clear ALL credit-related data for ${customer.name}?\n\n` +
      `This will:\n` +
      `- mark credit invoices as paid\n` +
      `- delete settlements (open + closed)\n` +
      `- delete reminders\n` +
      `- delete settlement closure logs\n\n` +
      `This cannot be undone.`
    );
    if (!ok) return;

    setClearingCredit(true);
    try {
      const d = await fetch(CLEAR_CREDIT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: parsedCustomerId,
          confirm_clear: true,
        }),
      }).then((r) => r.json());

      if (d?.status !== 'ok') {
        showSnack('error', d?.message || 'Failed to clear credit data');
        return;
      }

      const c = d.counts || {};
      showSnack(
        'success',
        `Cleared. Invoices marked paid: ${c.marked_paid_credit_invoices || 0}, settlements deleted: ${c.deleted_settlements || 0}, reminders deleted: ${c.deleted_reminders || 0}`
      );
      await loadHistory();
    } catch {
      showSnack('error', 'Network error while clearing data');
    } finally {
      setClearingCredit(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack} variant="outlined">
          Back To Credit List
        </Button>
        <Chip label={`Customer #${parsedCustomerId}`} size="small" />
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {customer?.name || fallbackName || 'Customer Credit Profile'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {customer?.phone || 'No phone'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <TextField
              label="From"
              type="date"
              size="small"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              startIcon={<RefreshIcon />}
              variant="contained"
              onClick={loadHistory}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <StatCard label="Open Credit" value={`Rs ${fmt(summary.open_credit_total)}`} color="warning.main" />
            <StatCard label="Open Invoices" value={String(summary.open_credit_count || unpaidInvoices.length)} />
            <StatCard label="Received (Open Cycle)" value={`Rs ${fmt(summary.settlement_open_total)}`} color="success.main" />
            <StatCard label="Balance" value={`Rs ${fmt(summary.open_balance)}`} color={Number(summary.open_balance || 0) > 0 ? 'error.main' : 'success.main'} />
            <StatCard label="Settled Cycles" value={String(summary.auto_closure_count || 0)} />
          </Stack>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Add Credit Sale</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                label="Date"
                type="date"
                size="small"
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Invoice / Order Ref"
                size="small"
                value={saleRef}
                onChange={(e) => setSaleRef(e.target.value)}
                placeholder="Example: 14-2210"
              />
              <TextField
                label="Amount"
                type="number"
                size="small"
                value={saleAmt}
                onChange={(e) => setSaleAmt(e.target.value)}
              />
              <TextField
                select
                label="Payment Method"
                size="small"
                value={saleMethod}
                onChange={(e) => setSaleMethod(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                {paymentMethodOptions.map((opt) => (
                  <MenuItem key={opt.value || 'empty'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={addCreditSale} disabled={savingSale}>
                {savingSale ? 'Saving...' : 'Add Credit Sale'}
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Receive Amount</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                label="Amount"
                type="number"
                size="small"
                value={rcvAmt}
                onChange={(e) => setRcvAmt(e.target.value)}
              />
              <TextField
                label="Date"
                type="date"
                size="small"
                value={rcvDate}
                onChange={(e) => setRcvDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Note"
                size="small"
                value={rcvNote}
                onChange={(e) => setRcvNote(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <Button variant="contained" onClick={addSettlement} disabled={savingSettlement}>
                {savingSettlement ? 'Saving...' : 'Add Settlement'}
              </Button>
              <Button
                startIcon={sendingCust ? <CircularProgress size={16} /> : <NotificationsActiveIcon />}
                variant="outlined"
                onClick={remindCustomer}
                disabled={sendingCust}
              >
                Remind Customer
              </Button>
              <Button
                color="success"
                startIcon={markingPaid ? <CircularProgress size={16} /> : <DoneAllIcon />}
                variant="contained"
                onClick={markAllAsPaid}
                disabled={markingPaid || unpaidInvoices.length === 0}
              >
                Mark All As Paid
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={clearCreditData}
                disabled={clearingCredit}
              >
                {clearingCredit ? 'Clearing...' : 'Clear Credit Data'}
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Unpaid Invoices</Typography>
            <Divider sx={{ my: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Reminder</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unpaidInvoices.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center">No unpaid invoices</TableCell></TableRow>
                ) : unpaidInvoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>{inv.order_details || `#${inv.id}`}</TableCell>
                    <TableCell>{(inv.created_at || '').split('T')[0]}</TableCell>
                    <TableCell align="right">Rs {fmt(inv.amount)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Send invoice reminder">
                        <span>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => remindInvoice(inv)}
                            disabled={sendingInvId === inv.id}
                          >
                            {sendingInvId === inv.id ? <CircularProgress size={16} /> : <NotificationImportantIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Settlement History (All)</Typography>
            <Divider sx={{ my: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allSettlements.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center">No settlements</TableCell></TableRow>
                ) : allSettlements.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.pay_date}</TableCell>
                    <TableCell align="right">Rs {fmt(s.amount)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={Number(s.closed) === 1 ? 'success' : 'warning'}
                        label={Number(s.closed) === 1 ? 'Closed Cycle' : 'Open Cycle'}
                      />
                    </TableCell>
                    <TableCell>{s.note || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="h6">Closure Logs</Typography>
              <Divider sx={{ my: 1 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Closed At</TableCell>
                    <TableCell align="right">Credit</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell>SMS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {closureLogs.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center">No closure logs</TableCell></TableRow>
                  ) : closureLogs.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.closed_at}</TableCell>
                      <TableCell align="right">Rs {fmt(c.open_credit_total)}</TableCell>
                      <TableCell align="right">Rs {fmt(c.received_total)}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={Number(c.sms_sent) === 1 ? 'success' : 'default'} label={Number(c.sms_sent) === 1 ? 'Sent' : 'Not Sent'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="h6">Recent Reminders</Typography>
              <Divider sx={{ my: 1 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sent At</TableCell>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Channel</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reminders.length === 0 ? (
                    <TableRow><TableCell colSpan={3} align="center">No reminders</TableCell></TableRow>
                  ) : reminders.slice(0, 20).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.sent_at}</TableCell>
                      <TableCell>{r.sale_id || 'Customer'}</TableCell>
                      <TableCell>{r.channel}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Stack>

          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6">Ledger Timeline</Typography>
            <Divider sx={{ my: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledger.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">No ledger records</TableCell></TableRow>
                ) : ledger.slice(-100).map((l, idx) => (
                  <TableRow key={`${l.type}-${l.sale_id || l.settlement_id || idx}`}>
                    <TableCell>{l.date}</TableCell>
                    <TableCell>{l.type === 'invoice' ? 'Invoice' : 'Settlement'}</TableCell>
                    <TableCell align="right">Rs {fmt(l.amount)}</TableCell>
                    <TableCell>{l.status || '-'}</TableCell>
                    <TableCell>{l.reference || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
